import { memo, useCallback, useLayoutEffect, useRef, useState } from 'react';

import { SEARCH_FOCUS_HIGHLIGHT_COLOR, SEARCH_HIGHLIGHT_COLOR } from '../constants';
import { findPageMatches, MatchRect, normalisedWordRects, OcrTextLine } from '../text-matching';
import { cn } from '@/new/lib/utils';
import { BoundingBoxWithId } from '@/types';

export type { OcrWord, OcrTextLine } from '../text-matching';

/**
 * Selectable / searchable text overlay for image-rendered document pages.
 *
 * The text is transparent so the underlying image still shows through, but it
 * lives in the real DOM, so native find-in-page and text selection both work.
 */

/**
 * Pixel-space rects for characters [start, end) of a line that HAS word boxes.
 * Built from the normalised (0..1) word rects scaled to the rendered page size.
 */
const wordRectsForRange = (
  words: OcrTextLine['words'],
  start: number,
  end: number,
  width: number,
  height: number,
): MatchRect[] =>
  normalisedWordRects(words ?? [], start, end).map((rect) => ({
    left: rect.left * width,
    top: rect.top * height,
    width: rect.width * width,
    height: rect.height * height,
  }));

/**
 * Rects for characters [start, end) of a line WITHOUT word boxes. Measured from
 * the rendered span via a DOM Range so the scaleX transform is included. This is a fallback, and
 * doesn't result in very accurate highlighting.
 *
 * Client rects are in visual px while our styles are in layout px, so the
 * ancestor zoom transform is divided back out via `visualScale`.
 */
const domRectsForRange = (
  span: HTMLSpanElement | null | undefined,
  start: number,
  end: number,
  containerRect: DOMRect,
  visualScale: number,
): MatchRect[] => {
  const textNode = span?.firstChild;
  if (!textNode) return [];

  const safeEnd = Math.min(end, textNode.textContent?.length ?? end);
  if (safeEnd <= start) return [];

  const range = document.createRange();
  range.setStart(textNode, start);
  range.setEnd(textNode, safeEnd);

  return Array.from(range.getClientRects()).map((rect) => ({
    left: (rect.left - containerRect.left) / visualScale,
    top: (rect.top - containerRect.top) / visualScale,
    width: rect.width / visualScale,
    height: rect.height / visualScale,
  }));
};

interface TextLayerProps {
  lines: OcrTextLine[];
  pageNumber: number;
  // Page dimensions
  width: number;
  height: number;
  highlights?: BoundingBoxWithId[];
  onHighlightClick?: (id: string) => void;
  searchQuery?: string;
  focusedMatchIndex?: number | null;
  disabled?: boolean;
}

// Memoised: none of its props change on zoom (the parent scales this layer
// with a CSS transform), so zoom ticks skip reconciling the per-line spans.
export const TextLayer = memo(function TextLayer({
  lines,
  pageNumber,
  width,
  height,
  highlights,
  onHighlightClick,
  searchQuery,
  focusedMatchIndex,
  disabled,
}: TextLayerProps) {
  const spanRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  // One entry per search match occurrence on the page
  const [matchGroups, setMatchGroups] = useState<MatchRect[][]>([]);

  // Highlights are clickable to select a fact, but the text layer overlays the image canvas, so we
  // need to identify any highlight clicks that hit the text layer
  const findClickableHighlightAt = useCallback(
    (clientX: number, clientY: number) => {
      const container = containerRef.current;
      if (!container || !highlights?.length) return undefined;

      const rect = container.getBoundingClientRect();
      const x = (clientX - rect.left) / rect.width;
      const y = (clientY - rect.top) / rect.height;

      return highlights.find(
        (h) =>
          h.pageNumber === pageNumber && x >= h.left && x <= h.left + h.width && y >= h.top && y <= h.top + h.height,
      );
    },
    [highlights, pageNumber],
  );

  // A drag leaves an active text selection; only treat a click as a highlight
  // click when the user didn't just select text.
  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!onHighlightClick) return;
      if (window.getSelection()?.toString()) return;

      const hit = findClickableHighlightAt(event.clientX, event.clientY);
      if (hit) onHighlightClick(hit.id);
    },
    [onHighlightClick, findClickableHighlightAt],
  );

  // Show a pointer over clickable highlights, a text caret everywhere else.
  const handleMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const container = containerRef.current;
      if (!container) return;
      container.style.cursor = findClickableHighlightAt(event.clientX, event.clientY) ? 'pointer' : 'text';
    },
    [findClickableHighlightAt],
  );

  // Each line's font-size is set from its OCR box height, which gets the
  // vertical scale roughly right but leaves the rendered width arbitrary.
  // After layout we measure each span and squash/stretch it horizontally so
  // its width matches the OCR box — this keeps selection rectangles aligned
  // with the words underneath them.
  //
  // Once the spans are positioned we also locate search matches. We match
  // against the whole page (not line by line) so a phrase can wrap across a
  // line break, then map each match back to the line(s) it covers. Per line we
  // highlight from per-word boxes when present (most accurate), else from the
  // rendered span via a DOM Range (scaleX transform included).
  useLayoutEffect(() => {
    // Batched into write -> read -> write phases: interleaving a
    // getBoundingClientRect() read after each style write forces a synchronous
    // reflow per line, which makes dense pages unusably slow.
    lines.forEach((_, index) => {
      const span = spanRefs.current[index];
      if (span) span.style.transform = '';
    });

    const container = containerRef.current;
    const containerRect = container?.getBoundingClientRect();
    // Client rects are in visual px; an ancestor scales this layer for zoom, so
    // divide measurements back into layout px before comparing/positioning.
    const visualScale = containerRect && containerRect.width > 0 && width > 0 ? containerRect.width / width : 1;
    const measuredWidths = lines.map((_, index) => spanRefs.current[index]?.getBoundingClientRect().width ?? 0);

    lines.forEach((line, index) => {
      const span = spanRefs.current[index];
      if (!span) return;

      const targetWidth = line.width * width;
      const actualWidth = measuredWidths[index] / visualScale;

      if (actualWidth > 0 && targetWidth > 0) {
        span.style.transform = `scaleX(${targetWidth / actualWidth})`;
      }
    });

    const matches = findPageMatches(lines, searchQuery);
    if (!containerRect || matches.length === 0) {
      setMatchGroups([]);
      return;
    }

    // One rect group per match. Per covered line we highlight from per-word
    // boxes when present (most accurate), else from the rendered span via a DOM
    // Range (scaleX transform included).
    const groups = matches.map((match) => {
      const rects: MatchRect[] = [];
      for (const span of match.spans) {
        const line = lines[span.lineIndex];
        if (line.words && line.words.length > 0) {
          rects.push(...wordRectsForRange(line.words, span.localStart, span.localEnd, width, height));
        } else {
          rects.push(
            ...domRectsForRange(
              spanRefs.current[span.lineIndex],
              span.localStart,
              span.localEnd,
              containerRect,
              visualScale,
            ),
          );
        }
      }
      return rects;
    });

    setMatchGroups(groups);
  }, [lines, width, height, searchQuery]);

  return (
    // The whole layer captures pointer events so a drag can select text
    // continuously across the page (the highlight canvas below is now
    // visual-only). It's disabled while the snipping tool is active so the
    // selection canvas underneath gets the drag instead.
    <div
      ref={containerRef}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      className={cn(
        'absolute inset-0 z-30 cursor-text overflow-hidden leading-none text-indigo-500',
        disabled ? 'pointer-events-none' : 'pointer-events-auto',
      )}
    >
      {/* Per-phrase search highlights. */}
      {matchGroups.map((rects, matchIndex) =>
        rects.map((rect, rectIndex) => (
          <div
            key={`${matchIndex}-${rectIndex}`}
            className="pointer-events-none absolute"
            style={{
              left: rect.left,
              top: rect.top,
              width: rect.width,
              height: rect.height,
              backgroundColor: matchIndex === focusedMatchIndex ? SEARCH_FOCUS_HIGHLIGHT_COLOR : SEARCH_HIGHLIGHT_COLOR,
            }}
          />
        )),
      )}

      {lines.map((line, index) => (
        <span
          key={index}
          ref={(el) => {
            spanRefs.current[index] = el;
          }}
          className="absolute origin-top-left whitespace-pre text-transparent"
          style={{
            left: line.left * width,
            top: line.top * height,
            fontSize: line.height * height,
          }}
        >
          {line.text}
        </span>
      ))}
    </div>
  );
});
